import React, { memo, useRef, useState } from "react";
import { Col, Popover, Row } from "antd";
import { useQueryClient } from "@tanstack/react-query";
import { SliderMarks } from "antd/lib/slider";
import videos from "video.js";
import moment from "moment/moment";

import VideoTimeline from "@Components/activity/VideoTimeline";
import VideoComment from "@Components/activity/VideoComment";
import VideoWrapperHooks from "@Components/activity/VideoWrapperHooks";
import UserIcon from "@Components/user/UserIcon";
import { IActivityDetails } from "@Models/IActivityDetails";
import { IQuestionnaireInstance, IQuestionnaireMinimal } from "@Models/IQuestionnaireDetails";
import { QueryKeys } from "@Constants";
import SessionManager from "@Core/session";

import { SafariDocument, SafariHTMLDivElement } from "types";
import CMEIndicator from "./CMEIndicator";

import "@Styles/pages/activityPage.scss";

interface IProps {
    activity?: IActivityDetails;
    isActive?: boolean;
    withoutComments?: boolean;
    isFetching?: boolean;
    isLoadingCreateQuestionnaire?: boolean;
    hasVideo?: boolean;
    questionnaireInstance?: IQuestionnaireInstance | IQuestionnaireMinimal;
    onAnswerQuestion?: (questionId: string, choiceId: string) => Promise<void>;
    onComplete?: () => Promise<string>;
    onGetStarted?: (activityOwner: string) => Promise<void>;
    associatedStatus?: { status: string };
    isHydrated?: boolean;
}

const IS_IOS_SAFARI = videos.browser.IS_SAFARI && videos.browser.IS_IOS;

const VideoWrapper: React.FC<IProps> = ({
    activity,
    isActive,
    hasVideo,
    withoutComments = false,
    onAnswerQuestion,
    onComplete,
    onGetStarted,
    isLoadingCreateQuestionnaire,
    isFetching,
    questionnaireInstance,
    associatedStatus,
    isHydrated,
}: IProps) => {
    /* isFullScreen: using for detection fullscreenMode and hiding comments section */
    const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
    const videoRef = React.useRef<HTMLDivElement>(null);

    const {
        progressState,
        setProgressState,
        marksState,
        setMarksState,
        commentsState,
        setCommentsState,
        durationState,
        setDurationState,
    } = VideoWrapperHooks();
    const { userId } = SessionManager.getServiceUser();

    const fullScreenContainer = useRef<SafariHTMLDivElement>(null);
    const toggleFullScreen = () => {
        setIsFullScreen(true);
        if (!fullScreenContainer.current) return null;

        if (fullScreenContainer.current?.requestFullscreen) {
            return fullScreenContainer.current?.requestFullscreen();
        }

        if (fullScreenContainer.current?.webkitRequestFullscreen) {
            return fullScreenContainer.current?.webkitRequestFullscreen();
        }
        return null;
    };

    const isOwner = activity?.userId === userId;

    const toggleFullScreenExit = () => {
        setIsFullScreen(false);
        if (IS_IOS_SAFARI && (document as SafariDocument)?.webkitExitFullscreen()) {
            (document as SafariDocument)?.webkitExitFullscreen();
        }
    };
    const queryClient = useQueryClient();

    /**
     * Render the video title
     * @param activityName
     * @param timestamp
     */
    const renderVideoTitle = (activityName, timestamp) => {
        return `${activityName} - ${moment(timestamp).format("LLLL")}`;
    };

    const onProgress = (value: number) => {
        setProgressState(value);
    };

    const onCommentsChanged = async () => {
        if (commentsState.length === 0) {
            return;
        }
        const marks: SliderMarks = {};
        marks[0] = "";
        commentsState.forEach((item) => {
            const position = (item.time / durationState) * 0.1;
            marks[position] = (
                <Popover overlayStyle={{ maxWidth: "300px" }} content={item.content} title={item.fullName}>
                    <div className={"bounce-in-bck"}>
                        <UserIcon fullName={item.fullName} size={24} />
                    </div>
                </Popover>
            );
        });
        marks[100] = "";
        setMarksState(marks);
        await queryClient.invalidateQueries([QueryKeys.ActivityCurrent, activity.activityInstanceId]);
    };

    const onDuration = (value: number) => {
        setDurationState(value);
        setCommentsState(activity.activityComments);
    };

    const options: videos.PlayerOptions = {
        controls: false,
        controlBar: false,
        muted: IS_IOS_SAFARI,
        bigPlayButton: false,
        sources: [
            {
                src: activity.mediaUrl,
                type: "application/x-mpegURL",
            },
        ],
        nativeControlsForTouch: false,
        html5: {
            nativeAudioTracks: false,
            nativeVideoTracks: false,
            hls: {
                // if enabled hls will determine segment size based on the connection speeed, not the window size
                // limitRenditionByPlayerDimensions: false,
            },
        },
    };

    const isNoComments = withoutComments || (isFullScreen && !commentsState.length);

    return (
        <Row style={{ padding: "5px" }} gutter={[16, 16]}>
            <Col md={24} lg={24} xl={isNoComments ? 24 : 14}>
                <div className="video-wrapper" ref={fullScreenContainer}>
                    <script type={"text/javascript"}>
                        window.VIDEOJS_NO_DYNAMIC_STYLE = true; window.HELP_IMPROVE_VIDEOJS = false;
                    </script>
                    <VideoTimeline
                        url={activity.mediaUrl}
                        thumbUrl={activity.thumbnailUrl}
                        marks={marksState}
                        userFullName={activity.userFullName}
                        title={renderVideoTitle(activity.activityLabel, activity.creationDate)}
                        onProgress={onProgress}
                        onDuration={onDuration}
                        toggleFullScreen={toggleFullScreen}
                        toggleFullScreenExit={toggleFullScreenExit}
                        options={options}
                        volumeEnabled={true}
                        isActive={isActive}
                        isCMEPublic={activity?.activityMeta?.isCMEPublic}
                        isFullScreen={isFullScreen}
                        ref={videoRef}
                        signatureItems={activity.activitySignature}
                    />
                </div>
            </Col>
            {isNoComments ? null : (
                <Col md={24} lg={24} xl={10} className="comment-cme-wrapper">
                    {questionnaireInstance && (isOwner || activity?.activityMeta?.isCMEPublic) ? (
                        <CMEIndicator
                            isFetching={isFetching}
                            isLoadingCreateQuestionnaire={isLoadingCreateQuestionnaire}
                            isCMEPublic={activity?.activityMeta?.isCMEPublic}
                            hasVideo={hasVideo}
                            onAnswerQuestion={onAnswerQuestion}
                            onComplete={onComplete}
                            onGetStarted={onGetStarted}
                            questionnaireInstanceData={questionnaireInstance}
                            associatedStatus={associatedStatus}
                            isAssociatedStatusHydrated={isHydrated}
                        />
                    ) : null}
                    <VideoComment
                        activityInstanceId={activity.activityInstanceId}
                        comments={commentsState}
                        setComments={setCommentsState}
                        progress={progressState}
                        duration={durationState}
                        onCommentsChanged={onCommentsChanged}
                    />
                </Col>
            )}
        </Row>
    );
};
export default memo(VideoWrapper);
